import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { format, subDays } from 'date-fns';
import type { SchedulerDateRange } from '../types/scheduler.types';
import { FILTER_RANGE_MAX_DAYS, sameWindow } from '../utils/displayWindow';
import { dateFilterRangeKey } from '../utils/dateFilterRange';
import type { ActiveDateFilterRange } from '../utils/dateFilterRange';

type Notify = (title: string, message: string) => void;

export type UseDateFilterNoticesOptions = {
  range: ActiveDateFilterRange | null;
  incomplete: string[];
  dateRange: SchedulerDateRange;
  /** Tasks on the board; blocked time does not answer a date filter. */
  taskCount: number;
  isLoadingSchedule: boolean;
  /** More resource pages exist, so an empty board is not yet known empty. */
  hasNextPage: boolean;
  notifyWarning: Notify;
};

type AwaitedResult = {
  target: SchedulerDateRange;
  /** The board's range when the filter applied, before it moved. */
  from: SchedulerDateRange;
};

function describeDays(range: SchedulerDateRange): string {
  const pattern = 'MMM d, yyyy';
  return `${format(range.startDate, pattern)} – ${format(
    subDays(range.endDate, 1),
    pattern
  )}`;
}

/** A ref that always holds the latest `value`, readable from effects. */
function useLatest<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * The warnings a date filter can raise: a range cut to the maximum, a filter
 * with only one end set, and a range with no matching tasks.
 */
export function useDateFilterNotices(
  options: UseDateFilterNoticesOptions
): void {
  // `useNotification` hands out new function identities every render.
  const notify = useLatest(options.notifyWarning);
  useLimitedRangeNotice(options.range, notify);
  useIncompleteFilterNotice(options.incomplete, notify);
  useEmptyResultNotice(options, notify);
}

function useLimitedRangeNotice(
  range: ActiveDateFilterRange | null,
  notify: MutableRefObject<Notify>
): void {
  const latestRange = useLatest(range);
  const rangeKey = dateFilterRangeKey(range);

  useEffect(() => {
    const current = latestRange.current;
    if (!current?.limited) return;
    notify.current(
      'Date range limited',
      `Scheduler View shows up to ${FILTER_RANGE_MAX_DAYS} days. Showing ${describeDays(
        current.window
      )} from the ${current.label} filter.`
    );
  }, [rangeKey, latestRange, notify]);
}

function useIncompleteFilterNotice(
  incomplete: string[],
  notify: MutableRefObject<Notify>
): void {
  const incompleteKey = incomplete.join(', ');

  useEffect(() => {
    if (incompleteKey === '') return;
    notify.current(
      'Incomplete date filter',
      `${incompleteKey} needs both a From and a To date. It was not applied.`
    );
  }, [incompleteKey, notify]);
}

/**
 * Judge emptiness only once the board shows the filtered days and every page
 * for them has loaded. Moving anywhere else first abandons the check.
 */
function useEmptyResultNotice(
  {
    range,
    dateRange,
    taskCount,
    isLoadingSchedule,
    hasNextPage,
  }: UseDateFilterNoticesOptions,
  notify: MutableRefObject<Notify>
): void {
  const latestRange = useLatest(range);
  const latestDateRange = useLatest(dateRange);
  const rangeKey = dateFilterRangeKey(range);
  const awaitedRef = useRef<AwaitedResult | null>(null);

  useEffect(() => {
    const current = latestRange.current;
    awaitedRef.current = current
      ? { target: current.window, from: latestDateRange.current }
      : null;
  }, [rangeKey, latestRange, latestDateRange]);

  useEffect(() => {
    const awaited = awaitedRef.current;
    if (!awaited) return;
    if (!sameWindow(awaited.target, dateRange)) {
      if (!sameWindow(awaited.from, dateRange)) awaitedRef.current = null;
      return;
    }
    if (isLoadingSchedule || hasNextPage) return;
    awaitedRef.current = null;
    if (taskCount === 0) {
      notify.current(
        'No matching tasks',
        `No tasks match the date filter for ${describeDays(awaited.target)}.`
      );
    }
  }, [dateRange, taskCount, isLoadingSchedule, hasNextPage, notify]);
}
